import { Button } from "GameObjects/UI/Button";
import * as Constants from "Data/Constants";
import * as HLF from "html-living-framework";
import * as Topics from "Data/Topics";
import * as SFX from "Data/SoundEffect";
import * as VO from "Data/VoiceOver";
import Settings from "Data/Settings";
import {DuckSwapGameLayout} from "Layouts/DuckSwapGameLayout";
import { LayoutSprite } from "GameObjects/UI/LayoutSprite";
import { Sine, TimelineMax, TweenLite } from "gsap";
import { LoadScreen } from "./LoadScreen";
import SettingsPopup from "Popups/SettingsPopup";
import { MainMenu } from "States/MainMenu";
import { DwellToggleButton } from "GameObjects/UI/DwellToggleButton";
import { LayoutButton } from "GameObjects/UI/LayoutButton";
import {HudLayout} from "Layouts/HudLayout";
import { Application } from "Application";

export abstract class AbstractGameState<T extends CallableFunction & ((this: AbstractGameState<T> & any) => AbstractGameState<T> & any)> extends HLF.State implements ReturnType<typeof HudLayout> {

    public abstract readonly layout: T;

    public readonly homeButton: LayoutButton;
    public readonly helpButton: LayoutButton;
    public readonly settingsButton: LayoutButton;
    public readonly dwellToggleButton: DwellToggleButton = new DwellToggleButton();
    public readonly characterContainer: LayoutSprite;

    protected homeButtonLeftMargin: number = 0;
    protected dwellToggleButtonRightMargin: number = 0;
    protected settingsButtonRightMargin: number = 0;
    protected helpButtonRightMargin: number = 0;
    protected characterContainerLeftMargin: number = 0;

    protected _subscriptions: any[] = [];

    protected abstract bgm: HLF.IAudioTrack;

    constructor() {
        super();

        this.homeButton = new LayoutButton(this.onHomeButtonPressed.bind(this), VO.HOME_BUTTON_HOVER, SFX.UI_CLICK);
        this.helpButton = new LayoutButton(()=>this.playHelpVO(), VO.BUTTON_HELP_HOVER, SFX.UI_CLICK);
        this.settingsButton = new LayoutButton(this.onSettingsButtonPressed.bind(this), VO.BUTTON_SETTINGS_HOVER, SFX.UI_CLICK);
        this.characterContainer = new LayoutSprite();

        this._subscriptions.push(
            PubSub.subscribe(Topics.SETTINGS_CHANGED, this.onSettingsChanged.bind(this)),
            PubSub.subscribe(HLF.GAME_PAUSED, this.onGamePaused.bind(this)),
            PubSub.subscribe(HLF.GAME_UNPAUSED, this.onGameUnPaused.bind(this)),
            PubSub.subscribe(HLF.VOICEOVER_STARTED, this.onVoiceoverStarted.bind(this)),
        );
    }

    public init(pSize: PIXI.Point, pData: {name: string}): void {
        this.addChild(this.characterContainer);
        this.addChild(this.homeButton);
        this.addChild(this.helpButton);
        this.addChild(this.settingsButton);
        this.addChild(this.dwellToggleButton);

        HudLayout.call(this);
        this.layout.call(this)

        this.homeButtonLeftMargin = this.homeButton.x;
        this.dwellToggleButtonRightMargin = 1024 - this.dwellToggleButton.x;
        this.settingsButtonRightMargin = 1024 - this.settingsButton.x;
        this.helpButtonRightMargin = 1024 - this.helpButton.x;
        this.characterContainerLeftMargin = this.characterContainer.x;

        this.name = pData.name;
        this.disableDwellOnInit();
        super.init(pSize); // calls onResize
        Application.instance.analytics.event("startLevel", pData.name);
    }

    public onResize(pSize: PIXI.Point): void {
        super.onResize(pSize);
        this.position.set((pSize.x - 1024) / 2, 0);

        this.homeButton.position.x = -((pSize.x - 1024) / 2) + this.homeButtonLeftMargin;
        this.dwellToggleButton.position.x = -((pSize.x - 1024) / 2) + pSize.x - this.dwellToggleButtonRightMargin;
        this.settingsButton.position.x = -((pSize.x - 1024) / 2) + pSize.x - this.settingsButtonRightMargin;
        this.helpButton.position.x = -((pSize.x - 1024) / 2) + pSize.x - (this.helpButtonRightMargin * Settings.buttonSizeMultiplier);
        this.characterContainer.position.x = -((pSize.x - 1024) / 2) + this.characterContainerLeftMargin;
    }

    public animateIn(pOnComplete: ()=>void) {
        super.animateIn(pOnComplete);
        if (!this.bgm.isPlaying()) {
            this.bgm.setLooped(true);
            this.bgm.play();
        }
        this.bgm.fadeTo(1, 500);
    }

    public animateOut(pOnComplete: ()=>void) {
        super.animateOut(pOnComplete);
        if (this.bgm.isPlaying()) {
            this.bgm.fadeTo(0, 500);
        }
    }

    public gotoNextState(name: string, data?: object) {
        HLF.Application.instance.voiceover.stopVO();
        PubSub.publishSync(HLF.STOP_CAPTION);
        PubSub.publishSync(HLF.UNREGISTER_ALL_FOCUSABLES);
        const steps = [
            HLF.TransitionStep.AnimCurrentOut,
            HLF.TransitionStep.ShowLoadScreen,
            HLF.TransitionStep.RemoveCurrent,
            HLF.TransitionStep.UnloadAssets,
            HLF.TransitionStep.LoadAssets,
            HLF.TransitionStep.AttachNewBehind,
            HLF.TransitionStep.AnimNewIn,
            HLF.TransitionStep.HideLoadScreen,
        ];
        PubSub.publishSync(
            HLF.STATE_LOAD_STATE, data ?
                new HLF.StateToken({id: name, data: {name, ...data}}, LoadScreen.NAME, ...steps) :
                new HLF.StateToken({id: name, data: {name}}, LoadScreen.NAME, ...steps),
        );
    }

    public destroy(pOptions: Parameters<typeof HLF.State.prototype.destroy>[0]) {
        this._subscriptions.forEach(PubSub.unsubscribe);
        super.destroy(pOptions);
    }

    protected disableDwellOnInit() {
        Settings.useDwellActivation = false;
    }

    protected abstract playHelpVO(): void;

    protected onSettingsChanged(pTopic: typeof Topics.SETTINGS_CHANGED, pSettings: Readonly<typeof Settings>): void {
        // delay call to onResize so buttons etc have already responded to the settings changed event
        PIXI.Ticker.shared.addOnce(()=>{
            if (!this._destroyed) {
                this.onResize(this._size);
                PubSub.publishSync(HLF.KEYBOARD_REFOCUS);
            }
        });
    }

    protected onGamePaused(): void {
        this.bgm.pause();
    }

    protected onGameUnPaused(): void {
        this.bgm.setVolume(1);
        if (!this.bgm.isPlaying()) {
            this.bgm.play();
        }
    }

    protected onSettingsButtonPressed(): void {
        this.bgm.pause();

        this.interactiveChildren = false;
        PubSub.publishSync(HLF.PUSH_KEYBOARD_LAYER);

        PubSub.publishSync(HLF.SHOW_POPUP, new HLF.PopupToken(
            SettingsPopup.NAME,
            this.onSettingsPopupClosed.bind(this),
            "static",
            false,
            {}
        ));
    }

    protected onHomeButtonPressed(): void {
        this.gotoNextState("MainMenu");
        Application.instance.analytics.event("endLevel", this.name);
    }

    protected onSettingsPopupClosed(): void {
        PubSub.publishSync(HLF.POP_KEYBOARD_LAYER);
        this.interactiveChildren = true;
        if (!this.bgm.isPlaying()) {
            this.bgm.play();
        }
        this.bgm.setVolume(1);
    }

    protected onVoiceoverStarted(pTopic: typeof HLF.VOICEOVER_STARTED, pData: string): void {
        if (pData.indexOf("jackie") === 0) {
            this.characterContainer.setLayoutTexture("cyberchase-flying-character-jackie", this.characterContainer.anchor.x, this.characterContainer.anchor.y);
        }
        else if (pData.indexOf("matt") === 0) {
            this.characterContainer.setLayoutTexture("cyberchase-flying-character-matt", this.characterContainer.anchor.x, this.characterContainer.anchor.y);
        }
        else if (pData.indexOf("inez") === 0) {
            this.characterContainer.setLayoutTexture("cyberchase-flying-character-inez", this.characterContainer.anchor.x, this.characterContainer.anchor.y);
        }
    }

}
